import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { CurrentUser } from "@/lib/types";

/**
 * POSTs /api/v1/auth/login/ with {email, password}.
 *
 * On success, the response body IS the freshly-authenticated user —
 * we drop it straight into the React Query cache under ["me"] so the
 * rest of the app (which calls `useCurrentUser`) picks it up without
 * a second round-trip.
 */
interface LoginInput {
  email: string;
  password: string;
}

interface LoginError {
  detail?: string;
  email?: string[];
  password?: string[];
  status?: number;
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation<CurrentUser, LoginError, LoginInput>({
    mutationFn: async (creds) => {
      // Make sure we have a fresh CSRF cookie before the POST.
      await api.get("/auth/csrf/");
      const { data } = await api.post<CurrentUser>("/auth/login/", creds);
      return data;
    },
    onSuccess: (user) => {
      qc.setQueryData(["me"], user);
    },
  });
}
